import {
   useCallback,
   useState,
   useEffect,
   useId,
   useRef,
   ReactNode,
   FormEvent,
   Suspense,
   RefObject,
} from 'react';
import type { ComponentProps, FocusEvent, KeyboardEvent, MouseEvent, PointerEvent } from 'react';
import { cn } from '@ifixit/ui/lib/utils';
import { LocaleCode } from '@ifixit/i18n/types';
// Import from our reorganized search directory
import { SearchInput } from '../search/components/search-input';
// Types for search functionality
import type { SearchClientType } from '../search/types';
import type { PopoverContent, PopoverInteractOutsideEvent } from './popover';
// Dynamic imports - centralized in a single file
import {
   DynamicSearchProvider,
   DynamicSearchContent,
   DynamicPopover,
   DynamicPopoverTrigger,
   DynamicPopoverContent,
   preloadAllSearchComponents,
} from '../search/utils/dynamic-imports';
import { getSearchUrl } from '../search/utils';
import { useSearchContext } from '../search/components/search-provider';
import { type Doctype } from '@ifixit/helpers';
import { SeeAllResultsFooter } from '../search/components/search-content';
import { useShortcut } from '@ifixit/utils-react/hooks';

// Types
// ---------------------------------------------------------------------------

/**
 * Props for the InstantSearch component
 */
export interface InstantSearchProps {
   canPopoverOpen?: boolean;
   /** Placeholder text for the search input */
   placeholder?: string;
   /** Initialized Algolia search client */
   searchClient: SearchClientType;
   /** Record mapping document types to arrays of index names */
   indices: Partial<Record<Doctype, string[]>>;
   /** Algolia public search API key */
   apiKey: string;
   isProUser: boolean;
   /** Ordered list of document types to control display order */
   inputProps?: ComponentProps<typeof SearchInput>['inputProps'];
   /** Additional props to pass to the Popover component */
   popoverProps?: Omit<ComponentProps<typeof PopoverContent>, 'onInteractOutside'>;
   /** Optional content to render after the input (like keyboard shortcut indicator) */
   inputAfter?: ReactNode;
   /** Additional class names to apply to the root container */
   className?: string;
   /** Currency code for product prices */
   currency: string;
   /** Locale code for internationalization */
   localeCode: LocaleCode;
   /** Store code for shop-specific content */
   storeCode: string;
   /** All known store codes, for building wiki store tag filters */
   allStoreCodes?: string[];
   /** Language identifier, defaults to 'en' */
   langid?: string;
   shortcutKey?: string;
   /** Autofocus the search input on mount */
   autoFocus?: boolean;
}

// Lazy-loaded components
// ---------------------------------------------------------------------------

/**
 * Custom hook to synchronize search state between provider and local component
 */
function useSyncSearchState(localQuery: string, setIsSearching: (value: boolean) => void) {
   const { query, setQuery, isSearching, hasResults } = useSearchContext();
   // Sync the provider's query with our local state
   useEffect(() => {
      if (query !== localQuery) {
         setQuery(localQuery);
      }
   }, [localQuery, setQuery, query]);

   // Update our local state from provider
   useEffect(() => {
      setIsSearching(isSearching);
   }, [isSearching, hasResults, localQuery, setIsSearching]);
}

/**
 * Consistent popover styling classes
 */
const POPOVER_STYLES = cn(
   'p-0 bg-white',
   'sm:min-w-[474px] z-100 sm:overflow-initial overflow-y-auto',
   'rounded-none sm:rounded-2xl w-screen sm:w-(--popover-trigger-width) mx-auto shadow-2xl'
);

/**
 * Loading overlay component that covers the entire search container
 */
function LoadingOverlay({ children, isLoading }: { children: ReactNode; isLoading: boolean }) {
   return (
      <div className="relative">
         {children}
         {isLoading && (
            <div
               className="absolute inset-0 bg-gray-50/50 flex items-center justify-center transition-all duration-200 ease-in-out z-10 pointer-events-none"
               data-testid="loading-overlay"
               role="status"
               aria-live="polite"
            />
         )}
      </div>
   );
}

/**
 * Container for search results content
 */
function SearchResultsContainer({
   currency,
   localeCode,
   storeCode,
}: {
   currency: string;
   localeCode: LocaleCode;
   storeCode: string;
}) {
   const { isSearching } = useSearchContext();

   return (
      <LoadingOverlay isLoading={isSearching}>
         <div className="sm:max-h-[min(calc(var(--popover-available-height)-42px),648px)] overflow-x-hidden">
            <div
               className={cn(
                  'content flex flex-col items-start gap-5 self-stretch overflow-y-auto',
                  'max-w-[100vw] sm:w-auto',
                  '[scrollbar-gutter:stable_both-edges] sm:[scrollbar-gutter:auto]'
               )}
            >
               <DynamicSearchContent
                  currency={currency}
                  localeCode={localeCode}
                  storeCode={storeCode}
               />
            </div>
            <SeeAllResultsFooter />
         </div>
      </LoadingOverlay>
   );
}

/**
 * Custom hook to preload components and manage loading state
 */
function useComponentPreloading() {
   const [componentsLoaded, setComponentsLoaded] = useState(false);

   useEffect(() => {
      void preloadAllSearchComponents().then(() => {
         setComponentsLoaded(true);
      });
   }, []);

   return componentsLoaded;
}

/**
 * Creates form submission handler
 */
function createSubmitHandler(localQuery: string, hasSearchText: boolean) {
   return (e: FormEvent) => {
      e.preventDefault();
      if (hasSearchText) {
         window.location.href = getSearchUrl({ searchStr: localQuery });
      }
   };
}

/**
 * Manages search result popover display and state synchronization
 */
function SearchResultsPopover({
   localQuery,
   setIsSearching,
   popoverProps,
   currency,
   localeCode,
   storeCode,
   areResultsRequested,
   onOpenChange,
}: {
   localQuery: string;
   setIsSearching: (value: boolean) => void;
   popoverProps?: Omit<ComponentProps<typeof PopoverContent>, 'onInteractOutside'>;
   currency: string;
   localeCode: LocaleCode;
   storeCode: string;
   areResultsRequested: boolean;
   onOpenChange: (open: boolean) => void;
}) {
   const { hasSearched } = useSearchContext();

   // Keep search provider and local state in sync
   useSyncSearchState(localQuery, setIsSearching);

   return (
      <DynamicPopover open={areResultsRequested && hasSearched} onOpenChange={onOpenChange}>
         <DynamicPopoverTrigger asChild>
            <div className="absolute w-full" />
         </DynamicPopoverTrigger>
         <DynamicPopoverContent
            className={POPOVER_STYLES}
            align="center"
            sideOffset={4}
            onOpenAutoFocus={e => e.preventDefault()}
            // The results follow the field in page order, so Tab walks into them from the
            // field and out of them onto the rest of the page, like any other content.
            loopFocus={false}
            data-testid="popover-content"
            {...popoverProps}
         >
            <SearchResultsContainer
               currency={currency}
               localeCode={localeCode}
               storeCode={storeCode}
            />
         </DynamicPopoverContent>
      </DynamicPopover>
   );
}

/**
 * Main InstantSearch component
 *
 * This component provides a search input with a dropdown of search results.
 * It uses dynamic imports for the search functionality and UI components to
 * improve initial page load performance.
 *
 * The component renders the search input immediately and loads the search
 * results dropdown dynamically when the input is focused.
 */
export function InstantSearch({
   placeholder,
   searchClient,
   apiKey,
   indices,
   inputProps,
   popoverProps,
   inputAfter,
   className,
   currency,
   localeCode,
   storeCode,
   allStoreCodes,
   langid = 'en',
   canPopoverOpen = true,
   shortcutKey,
   isProUser,
   autoFocus = false,
}: InstantSearchProps) {
   // Component state
   const rootRef = useRef<HTMLDivElement>(null);
   const inputRef = useRef<HTMLInputElement>(null);
   const generatedInputId = useId();
   const inputId = inputProps?.id ?? generatedInputId;
   const [localQuery, setLocalQuery] = useState('');
   const [isSearching, setIsSearching] = useState(false);
   const [areResultsRequested, setAreResultsRequested] = useState(false);
   // Closing unmounts the results. They close on:
   // - Escape anywhere while they're open (the popover hears it on window); Escape in
   //   the field also closes them while loading, and empties the field. Its blur
   //   has no relatedTarget, so nothing else closes them.
   // - Focus leaving the search for the page, even before they open, or they would
   //   cover where focus went. Focus leaving for another tab or window keeps them.
   // - A press outside the search, even before they open. Safari doesn't focus a pressed
   //   button or link, so the field's blur has no relatedTarget and looks like a switch
   //   to another window.
   const openResults = useCallback(() => setAreResultsRequested(true), []);
   const closeResults = useCallback(() => setAreResultsRequested(false), []);

   // Autofocus effect
   useEffect(() => {
      if (autoFocus && inputRef.current) {
         inputRef.current.focus();
      }
   }, [autoFocus]);

   // Component loading management
   const componentsLoaded = useComponentPreloading();
   const ShortcutIndicator = useShortcutIndicator(shortcutKey, inputRef);
   useCloseOnPressOutside(rootRef, areResultsRequested, closeResults);

   // Derived state
   const hasSearchText = localQuery.trim() !== '';

   // Event handlers
   const handleSubmit = createSubmitHandler(localQuery, hasSearchText);
   const clearSearch = () => setLocalQuery('');

   const handlePopoverOpenChange = (open: boolean) => {
      if (open) {
         openResults();
         return;
      }
      // Unmounting the results would drop focus on a result to the page.
      inputRef.current?.focus({ preventScroll: true });
      closeResults();
   };
   // Typing reopens results Escape closed; the focused field gets no focus event.
   const handleQueryChange = (query: string) => {
      setLocalQuery(query);
      openResults();
   };

   const handleInputFocus = (e: FocusEvent<HTMLInputElement>) => {
      openResults();
      if (inputProps && inputProps.onFocus) {
         inputProps.onFocus(e);
      }
   };
   // After Escape on a result, the field keeps focus, so a click on it sends no focus
   // event to reopen the results.
   const handleInputPointerDown = (e: PointerEvent<HTMLInputElement>) => {
      openResults();
      inputProps?.onPointerDown?.(e);
   };
   const handleInputKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
      inputProps?.onKeyDown?.(e);
      if (e.key === 'Escape') {
         inputRef.current?.blur();
         clearSearch();
         closeResults();
      }
   };
   // The results render inside the root, so focus moving into them stays inside. A
   // switch to another tab or window has no relatedTarget.
   const closeOnFocusLeave = (e: FocusEvent<HTMLDivElement>) => {
      if (e.relatedTarget && !e.currentTarget.contains(e.relatedTarget)) {
         closeResults();
      }
   };
   const fieldProps = {
      ...inputProps,
      id: inputId,
      onFocus: handleInputFocus,
      onPointerDown: handleInputPointerDown,
      onKeyDown: handleInputKeyDown,
      autoFocus,
   };

   const mergedPopoverProps = {
      // The trigger is an empty anchor, so the search field names the results.
      'aria-labelledby': inputId,
      ...popoverProps,
      // useCloseOnPressOutside and closeOnFocusLeave own closing.
      onInteractOutside: (e: PopoverInteractOutsideEvent) => e.preventDefault(),
      // A press on a result would blur the field, which fires `change` on it and runs
      // the page's listeners for it inside the tap. Cancelling the press keeps focus
      // in the field; the click still follows.
      onMouseDown: (e: MouseEvent<HTMLDivElement>) => {
         popoverProps?.onMouseDown?.(e);
         e.preventDefault();
      },
   };

   // Mounting the results starts the search. The popover opens once it comes back.
   const shouldMountResults = componentsLoaded && canPopoverOpen && areResultsRequested;
   // isSearching is only updated from inside the results, which unmount when they close,
   // so it can be left true by a search that was still loading.
   const showLoadingSpinner =
      (isSearching && shouldMountResults) || (hasSearchText && !componentsLoaded);

   return (
      <div
         ref={rootRef}
         className={cn('relative flex-grow', className)}
         onClick={e => e.stopPropagation()}
         onBlur={closeOnFocusLeave}
      >
         <div className="w-full relative">
            <SearchInput
               query={localQuery}
               setQuery={handleQueryChange}
               clearSearch={clearSearch}
               placeholder={placeholder || ''}
               inputRef={inputRef}
               popoverLoading={showLoadingSpinner}
               inputProps={fieldProps}
               inputAfter={inputAfter || ShortcutIndicator}
               onSubmit={handleSubmit}
            />

            {shouldMountResults && (
               <Suspense fallback={null}>
                  <DynamicSearchProvider
                     searchOptions={{
                        searchClient,
                        apiKey,
                        indices,
                        langid,
                        isProUser,
                        storeCode,
                        allStoreCodes,
                     }}
                  >
                     <SearchResultsPopover
                        localQuery={localQuery}
                        setIsSearching={setIsSearching}
                        popoverProps={mergedPopoverProps}
                        currency={currency}
                        localeCode={localeCode}
                        storeCode={storeCode}
                        areResultsRequested={areResultsRequested}
                        onOpenChange={handlePopoverOpenChange}
                     />
                  </DynamicSearchProvider>
               </Suspense>
            )}
         </div>
      </div>
   );
}

/**
 * Visual indicator showing the keyboard shortcut for focusing the search input
 */
function SearchInputShortcut({ shortcutKey = '/' }: { shortcutKey?: string }) {
   return (
      <div
         className="absolute right-3 top-1/2 transform -translate-y-1/2 pointer-events-none"
         data-testid="shortcut-hint"
      >
         <div className="bg-gray-300 rounded-full px-2.5 py-1 text-xs text-gray-800">
            {shortcutKey}
         </div>
      </div>
   );
}

/**
 * Hook that provides keyboard shortcut functionality and returns the shortcut indicator
 */
function useShortcutIndicator(
   shortcutKey: string | undefined,
   inputRef: RefObject<HTMLInputElement | null>
) {
   const callback = (input: HTMLInputElement, event: globalThis.KeyboardEvent) => {
      input.focus();
      event.stopPropagation();
      event.preventDefault();
   };
   useShortcut({ key: shortcutKey }, inputRef, callback);
   return shortcutKey ? <SearchInputShortcut shortcutKey={shortcutKey} /> : null;
}

/**
 * Closes the results on a press outside the search. It listens from the moment they're
 * requested, since the popover only listens once open.
 */
function useCloseOnPressOutside(
   rootRef: RefObject<HTMLDivElement | null>,
   areResultsRequested: boolean,
   closeResults: () => void
) {
   useEffect(() => {
      if (!areResultsRequested) return;
      // A touch outside may start a scroll, so it closes on the tap's click, as the
      // popover does.
      const handlePointerDown = (event: globalThis.PointerEvent) => {
         document.removeEventListener('click', closeResults, { capture: true });
         if (event.target instanceof Node && rootRef.current?.contains(event.target)) return;
         if (event.pointerType === 'touch') {
            document.addEventListener('click', closeResults, { capture: true, once: true });
         } else {
            closeResults();
         }
      };
      document.addEventListener('pointerdown', handlePointerDown, { capture: true });
      return () => {
         document.removeEventListener('pointerdown', handlePointerDown, { capture: true });
         document.removeEventListener('click', closeResults, { capture: true });
      };
   }, [rootRef, areResultsRequested, closeResults]);
}
